"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ENDPOINT_FIELDS = void 0;
exports.createFlight = createFlight;
/** What endpoint() writes to `out`, in order. */
exports.ENDPOINT_FIELDS = ["t", "x", "y", "z", "vx", "vy", "vz", "apexZ"];
const MAX_DURATION = 5; // s, v1 simParams.duration
const DEFAULT_UNTIL = { kind: "bounces", count: 3 }; // v1 simParams.bounces
const OUT_OF_BOUNDS_MARGIN = 0.1; // m, v1 _ballOutOfBounds
/** rk4: a ground bounce lower than this ends the bouncing; the ball rolls. */
const REST_HEIGHT = 1e-4; // m
function createFlight(profile, options = {}) {
    const integrator = options.integrator ?? "rk4";
    const timeStep = options.timeStep ?? (integrator === "rk4" ? 0.02 : 0.001);
    if (!(timeStep > 0))
        throw new RangeError(`timeStep must be positive, got ${timeStep}`);
    const world = buildWorld(profile, options.spinModel ?? (integrator === "rk4" ? "physical" : "v1"), integrator);
    const worlds = {
        all: world,
        ground: { ...world, panels: [], legacy: NO_LEGACY_PANELS },
        none: { ...world, panels: [], legacy: NO_LEGACY_PANELS, ground: null },
    };
    const scratch = createScratch();
    const run = integrator === "rk4" ? runRk4 : runLegacy;
    const fly = (release, flyOptions = {}) => {
        const log = createLog(flyOptions.path ?? false);
        const surfaces = flyOptions.surfaces ?? "all";
        const stop = toStop(flyOptions.until ?? DEFAULT_UNTIL, (flyOptions.stopOutOfBounds ?? true) && surfaces !== "none");
        run(worlds[surfaces], timeStep, release, stop, log.recorder, scratch);
        const { result } = scratch;
        return {
            origin: release.origin,
            velocity: release.velocity,
            contacts: log.contacts,
            end: scratch.ended
                ? { t: result[END_T], position: { x: result[END_X], y: result[END_Y], z: result[END_Z] } }
                : null,
            apex: { t: result[APEX_T], position: { x: result[APEX_X], y: result[APEX_Y], z: result[APEX_Z] } },
            duration: result[DURATION],
            ...(log.path ? { path: log.path() } : {}),
            stoppedBy: scratch.stoppedBy,
        };
    };
    const endpointStop = { goal: "landing", value: 0, stopOutOfBounds: true };
    const endpoint = (release, until, out, surfaces = "all") => {
        if (typeof until === "object") {
            if (integrator !== "rk4")
                throw new RangeError("endpoint({ time }) needs the rk4 integrator");
            endpointStop.goal = "time";
            endpointStop.value = until.time;
            endpointStop.stopOutOfBounds = surfaces !== "none";
            run(worlds[surfaces], timeStep, release, endpointStop, null, scratch);
            if (scratch.stoppedBy !== "until")
                return false;
            // rk4 leaves the ball's state at the stop in scratch.start
            const { start, result } = scratch;
            out[0] = result[DURATION];
            for (let i = 0; i < 6; i++)
                out[1 + i] = start[i];
            out[7] = result[APEX_Z];
            return true;
        }
        endpointStop.goal = until === "landing" ? "landing" : "descending";
        endpointStop.value = until === "landing" ? 0 : until;
        endpointStop.stopOutOfBounds = surfaces !== "none";
        run(worlds[surfaces], timeStep, release, endpointStop, null, scratch);
        if (!scratch.ended)
            return false;
        copy(scratch.result, out, exports.ENDPOINT_FIELDS.length);
        return true;
    };
    const record = (release, recorder, flyOptions = {}) => {
        const surfaces = flyOptions.surfaces ?? "all";
        const stop = toStop(flyOptions.until ?? DEFAULT_UNTIL, (flyOptions.stopOutOfBounds ?? true) && surfaces !== "none");
        run(worlds[surfaces], timeStep, release, stop, recorder, scratch);
        return scratch.stoppedBy;
    };
    return { integrator, timeStep, spinModel: world.spinModel, fly, endpoint, record };
}
const NO_LEGACY_PANELS = { walls: [], net: null };
/** The panels v1 has, by id, and which side of each is the court. */
const V1_WALLS = [
    { id: "leftWall", axis: 0, inward: 1 },
    { id: "rightWall", axis: 0, inward: -1 },
    { id: "farWall", axis: 1, inward: -1 },
];
function buildWorld({ ball, venue, environment }, spinModel, integrator) {
    const { arena } = venue;
    const radius = ball.diameter / 2.0;
    const area = Math.PI * radius * radius;
    const dragCoefficient = 0.5 * environment.airDensity * ball.dragCoefficient * area;
    return {
        gravity: environment.gravity,
        mass: ball.mass,
        radius,
        dragPerMass: -1.0 * (dragCoefficient / ball.mass),
        magnusCoefficient: 0.5 * environment.airDensity * area,
        liftCoefficient: ball.liftCoefficient,
        spinModel,
        ground: { restitution: arena.ground.restitution },
        panels: integrator === "rk4" ? arena.panels.map(panelPlane) : [],
        legacy: integrator === "legacy" ? legacyPanels(arena) : NO_LEGACY_PANELS,
        reach: {
            minX: arena.bounds.x[0] - OUT_OF_BOUNDS_MARGIN,
            maxX: arena.bounds.x[1] + OUT_OF_BOUNDS_MARGIN,
            minY: arena.bounds.y[0] - OUT_OF_BOUNDS_MARGIN,
            maxY: arena.bounds.y[1] + OUT_OF_BOUNDS_MARGIN,
        },
    };
}
function panelPlane(panel) {
    const { from, to, response } = panel;
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const length = Math.hypot(dx, dy);
    if (!(length > 0 && Number.isFinite(length)))
        throw new RangeError(`panel ${panel.id} must have a finite, positive length`);
    const alongX = dx / length;
    const alongY = dy / length;
    const midX = (from.x + to.x) / 2;
    const midY = (from.y + to.y) / 2;
    const bounce = response.kind === "bounce" ? response : null;
    return {
        id: panel.id,
        kind: panel.kind,
        midX,
        midY,
        alongX,
        alongY,
        halfLength: length / 2,
        normalX: -alongY,
        normalY: alongX,
        offset: midX * -alongY + midY * alongX,
        stopsLeft: panel.faces !== "right",
        stopsRight: panel.faces !== "left",
        bottom: panel.bottom,
        top: panel.top,
        rise: (panel.topAtEnds ?? panel.top) - panel.top,
        absorbs: bounce === null,
        restitution: bounce?.restitution ?? 0,
        tangential: bounce?.tangential ?? 0,
    };
}
/**
 * v1's walls and net, read from the arena by id; the other panels are
 * ignored. v1's rules need its own geometry: planes along the axes, walls
 * that bounce.
 */
function legacyPanels({ panels }) {
    const walls = V1_WALLS.flatMap(({ id, axis, inward }) => {
        const panel = panels.find((p) => p.id === id);
        if (panel === undefined)
            return [];
        return [
            {
                id,
                kind: panel.kind,
                axis,
                at: alignedAt(panel, axis),
                inward,
                height: panel.top,
                restitution: legacyBounce(panel).restitution,
            },
        ];
    });
    const net = panels.find((p) => p.id === "net");
    return {
        walls,
        net: net === undefined ? null : { id: net.id, y: alignedAt(net, 1), height: net.top, ...legacyBounce(net) },
    };
}
/** Where an axis-aligned panel's plane meets `axis`: x = at (axis 0) or y = at (axis 1). */
function alignedAt({ id, from, to }, axis) {
    const [at, end] = axis === 0 ? [from.x, to.x] : [from.y, to.y];
    if (at !== end)
        throw new RangeError(`legacy: panel ${id} must lie along ${axis === 0 ? "x" : "y"} = constant, as v1's does`);
    return at;
}
function legacyBounce({ id, response }) {
    if (response.kind !== "bounce")
        throw new RangeError(`legacy: panel ${id} must bounce, as v1's do`);
    return { restitution: response.restitution, tangential: response.tangential };
}
function toStop(until, stopOutOfBounds) {
    switch (until.kind) {
        case "landing":
            return { goal: "landing", value: 0, stopOutOfBounds };
        case "descending":
            return { goal: "descending", value: until.z, stopOutOfBounds };
        case "bounces":
            return { goal: "bounces", value: until.count, stopOutOfBounds };
        case "time":
            return { goal: "time", value: until.seconds, stopOutOfBounds };
    }
}
const SPIN_AXIS = 0; // x, y, z: the unit v1 magnus axis
const SPIN_ANGULAR_SPEED = 3; // rad/s
const SPIN_MAGNUS = 4; // the Magnus coefficient, 0 without spin as in v1
const END_T = 0;
const END_X = 1;
const END_Y = 2;
const END_Z = 3;
const END_V = 4; // vx, vy, vz
const APEX_Z = 7;
const APEX_T = 8;
const APEX_X = 9;
const APEX_Y = 10;
const DURATION = 11;
const STEP_START = 0;
const STEP_LENGTH = 1;
const STEP_EVENT = 2;
const PLANE_OFFSET = 3; // after the normal's x, y, z
function createScratch() {
    return {
        spin: new Float64Array(5),
        rolling: false,
        result: new Float64Array(12),
        ended: false,
        stoppedBy: "until",
        step: new Float64Array(3),
        event: NO_EVENT,
        plane: new Float64Array(4),
        onPlane: false,
        leaving: NO_EVENT,
        start: new Float64Array(6),
        end: new Float64Array(6),
        startAcceleration: new Float64Array(3),
        endAcceleration: new Float64Array(3),
        velocities: new Float64Array(12),
        accelerations: new Float64Array(12),
    };
}
function prepare(world, release, scratch) {
    const { spin, result } = scratch;
    spin[SPIN_AXIS] = release.spinAxis.x;
    spin[SPIN_AXIS + 1] = release.spinAxis.y;
    spin[SPIN_AXIS + 2] = release.spinAxis.z;
    spin[SPIN_ANGULAR_SPEED] = (release.spinRpm / 60.0) * 2 * Math.PI;
    spin[SPIN_MAGNUS] = release.spinRpm === 0 ? 0 : world.magnusCoefficient;
    scratch.rolling = false;
    scratch.ended = false;
    scratch.stoppedBy = "until";
    scratch.leaving = NO_EVENT;
    result[APEX_T] = 0;
    result[APEX_X] = release.origin.x;
    result[APEX_Y] = release.origin.y;
    result[APEX_Z] = release.origin.z;
}
function finish(scratch, t, x, y, z, vx, vy, vz) {
    const { result } = scratch;
    scratch.ended = true;
    result[END_T] = t;
    result[END_X] = x;
    result[END_Y] = y;
    result[END_Z] = z;
    result[END_V] = vx;
    result[END_V + 1] = vy;
    result[END_V + 2] = vz;
}
function raiseApex(result, t, x, y, z) {
    if (z > result[APEX_Z]) {
        result[APEX_T] = t;
        result[APEX_X] = x;
        result[APEX_Y] = y;
        result[APEX_Z] = z;
    }
}
function stop(scratch, reason, t) {
    scratch.stoppedBy = reason;
    scratch.result[DURATION] = t;
}
/** Element copy: TypedArray.prototype.set costs more than the copy at these sizes. */
function copy(from, to, length) {
    for (let i = 0; i < length; i++)
        to[i] = from[i];
}
// ---------------------------------------------------------------------------
// The force model
/**
 * The ball's acceleration at the velocity in v[from..from+2], written to
 * out[to..to+2]. The arithmetic follows v1's _tick operation for operation;
 * the legacy integrator depends on that for bit-exactness.
 */
function accelerate(world, scratch, v, from, out, to) {
    const { spin } = scratch;
    const vx = v[from];
    const vy = v[from + 1];
    const vz = v[from + 2];
    const speed = Math.sqrt(vx * vx + vy * vy + vz * vz);
    let magnusX = 0;
    let magnusY = 0;
    let magnusZ = 0;
    if (spin[SPIN_MAGNUS] !== 0) {
        const axisX = spin[SPIN_AXIS];
        const axisY = spin[SPIN_AXIS + 1];
        const axisZ = spin[SPIN_AXIS + 2];
        let dirX = axisY * vz - vy * axisZ;
        let dirY = -(axisX * vz - vx * axisZ);
        let dirZ = axisX * vy - vx * axisY;
        // v1 lifts at full strength along the cross product's direction at any
        // angle; physically lift goes with omega x v, i.e. |axis x v| / |v|.
        // (A ball at rest has no cross product either; 1 keeps that at 0, not NaN.)
        const norm = world.spinModel === "v1" ? Math.sqrt(dirX * dirX + dirY * dirY + dirZ * dirZ) : speed || 1;
        dirX /= norm;
        dirY /= norm;
        dirZ /= norm;
        const lift = world.liftCoefficient(world.radius, spin[SPIN_ANGULAR_SPEED], speed);
        const force = spin[SPIN_MAGNUS] * lift * speed * speed;
        magnusX = (force * dirX) / world.mass;
        magnusY = (force * dirY) / world.mass;
        magnusZ = (force * dirZ) / world.mass;
    }
    const drag = world.dragPerMass * speed;
    out[to] = drag * vx + magnusX;
    out[to + 1] = drag * vy + magnusY;
    out[to + 2] = scratch.rolling ? 0 : drag * vz - world.gravity + magnusZ;
}
// ---------------------------------------------------------------------------
// legacy: v1's scheme
function runLegacy(world, dt, release, { goal, value, stopOutOfBounds }, recorder, scratch) {
    prepare(world, release, scratch);
    const { result, spin } = scratch;
    const { ground, reach } = world;
    const { walls, net } = world.legacy;
    const capT = goal === "time" ? Math.min(value, MAX_DURATION) : MAX_DURATION;
    // accelerate()'s inputs, hoisted: the step below repeats its arithmetic
    // on locals, which V8 keeps in registers (the call and its typed-array
    // round trip cost v1's own loop's time again).
    const { gravity, mass, radius, dragPerMass, liftCoefficient } = world;
    const v1Lift = world.spinModel === "v1";
    const magnus = spin[SPIN_MAGNUS];
    const axisX = spin[SPIN_AXIS];
    const axisY = spin[SPIN_AXIS + 1];
    const axisZ = spin[SPIN_AXIS + 2];
    const angularSpeed = spin[SPIN_ANGULAR_SPEED];
    let x = release.origin.x;
    let y = release.origin.y;
    let z = release.origin.z;
    let vx = release.velocity.x;
    let vy = release.velocity.y;
    let vz = release.velocity.z;
    let contacts = 0;
    let landed = false;
    let previousT = 0;
    let time = 0;
    recorder?.point(0, x, y, z, vx, vy, vz);
    // A per-iteration `let time` in the for header would cost V8 an allocation per step.
    for (;;) {
        time += dt;
        // accelerate(), operation for operation (the legacy loop is never rolling)
        const speed = Math.sqrt(vx * vx + vy * vy + vz * vz);
        let magnusX = 0;
        let magnusY = 0;
        let magnusZ = 0;
        if (magnus !== 0) {
            let dirX = axisY * vz - vy * axisZ;
            let dirY = -(axisX * vz - vx * axisZ);
            let dirZ = axisX * vy - vx * axisY;
            const norm = v1Lift ? Math.sqrt(dirX * dirX + dirY * dirY + dirZ * dirZ) : speed || 1;
            dirX /= norm;
            dirY /= norm;
            dirZ /= norm;
            const force = magnus * liftCoefficient(radius, angularSpeed, speed) * speed * speed;
            magnusX = (force * dirX) / mass;
            magnusY = (force * dirY) / mass;
            magnusZ = (force * dirZ) / mass;
        }
        const drag = dragPerMass * speed;
        const ax = drag * vx + magnusX;
        const ay = drag * vy + magnusY;
        const az = drag * vz - gravity + magnusZ;
        vx = vx + ax * dt;
        vy = vy + ay * dt;
        vz = vz + az * dt;
        const px = x;
        const py = y;
        const pz = z;
        x = x + vx * dt + 0.5 * ax * dt * dt;
        y = y + vy * dt + 0.5 * ay * dt * dt;
        z = z + vz * dt + 0.5 * az * dt * dt;
        const inVx = vx;
        const inVy = vy;
        const inVz = vz;
        // v1 _detectBounce: no interpolation; the sample is moved onto the surface.
        const hitGround = ground !== null && z <= 0.0;
        if (hitGround) {
            z = 0.0;
            vz = -1 * vz * ground.restitution;
        }
        // v1's walls are endless planes and leave the velocity along them alone.
        let hitWall = null;
        for (const wall of walls) {
            const p = wall.axis === 0 ? x : y;
            const outside = wall.inward > 0 ? p <= wall.at : p >= wall.at;
            if (outside && z < wall.height) {
                if (wall.axis === 0) {
                    x = wall.at;
                    vx = -1 * vx * wall.restitution;
                }
                else {
                    y = wall.at;
                    vy = -1 * vy * wall.restitution;
                }
                hitWall = wall;
                break;
            }
        }
        // v1's net has no width, a flat top, and only stops balls travelling toward +y.
        const hitNet = net !== null && py < net.y && y >= net.y && z < net.height;
        if (hitNet) {
            y = net.y;
            vy = -1 * vy * net.restitution;
            vx = vx * net.tangential;
            vz = vz * net.tangential;
        }
        raiseApex(result, time, x, y, z);
        recorder?.point(time, x, y, z, inVx, inVy, inVz);
        if (hitGround || hitWall !== null || hitNet)
            recorder?.deflect(vx, vy, vz);
        if (hitGround)
            recorder?.contact(time, x, y, z, "ground", "ground");
        if (hitWall !== null)
            recorder?.contact(time, x, y, z, hitWall.id, hitWall.kind);
        if (hitNet)
            recorder?.contact(time, x, y, z, net.id, "net");
        contacts += (hitGround ? 1 : 0) + (hitWall !== null ? 1 : 0) + (hitNet ? 1 : 0);
        const firstLanding = hitGround && !landed;
        landed ||= hitGround;
        if (goal === "descending") {
            // v1 _crossingPoint: linear between samples, searched up to the landing sample.
            if (pz >= value && z < value) {
                const f = (pz - value) / (pz - z);
                const t = previousT + f * (time - previousT);
                finish(scratch, t, px + f * (x - px), py + f * (y - py), value, inVx, inVy, inVz);
                recorder?.truncate(t, result[END_X], result[END_Y], value);
                return stop(scratch, "until", t);
            }
            if (firstLanding)
                return stop(scratch, "until", time);
        }
        else if (firstLanding) {
            finish(scratch, time, x, y, z, inVx, inVy, inVz);
        }
        if (goal === "landing" && firstLanding)
            return stop(scratch, "until", time);
        // v1 stops on exactly this many contacts; a step that overshoots the count flies on.
        if (goal === "bounces" && contacts === value)
            return stop(scratch, "until", time);
        if (goal === "time" && time >= capT && capT === value)
            return stop(scratch, "until", time);
        if (stopOutOfBounds && (x > reach.maxX || x < reach.minX || y > reach.maxY || y < reach.minY)) {
            return stop(scratch, "outOfBounds", time);
        }
        if (time >= capT)
            return stop(scratch, "timeLimit", time);
        previousT = time;
    }
}
// ---------------------------------------------------------------------------
// rk4: classical Runge-Kutta with exact events
/** Events other than a panel (a panel is its index in world.panels). */
const NO_EVENT = -1;
const GROUND = -2;
const OUT_OF_BOUNDS = -3;
const DESCENDING = -4;
function runRk4(world, dt, release, stopAt, recorder, scratch) {
    prepare(world, release, scratch);
    const { step, start, end, startAcceleration, endAcceleration } = scratch;
    const { goal, value } = stopAt;
    const capT = goal === "time" ? Math.min(value, MAX_DURATION) : MAX_DURATION;
    const capReason = goal === "time" && capT === value ? "until" : "timeLimit";
    setState(start, release.origin, release.velocity);
    accelerate(world, scratch, start, 3, startAcceleration, 0);
    recorder?.point(0, start[0], start[1], start[2], start[3], start[4], start[5]);
    step[STEP_START] = 0;
    let contacts = 0;
    for (;;) {
        const remaining = capT - step[STEP_START];
        if (remaining <= 1e-12)
            return stop(scratch, capReason, step[STEP_START]);
        const last = remaining <= dt;
        step[STEP_LENGTH] = last ? remaining : dt;
        rk4Step(world, scratch, end);
        accelerate(world, scratch, end, 3, endAcceleration, 0);
        const event = findEvent(world, scratch, stopAt);
        recordApex(scratch);
        if (event === NO_EVENT) {
            step[STEP_START] = last ? capT : step[STEP_START] + step[STEP_LENGTH];
            copy(end, start, 6);
            copy(endAcceleration, startAcceleration, 3);
            recorder?.point(step[STEP_START], start[0], start[1], start[2], start[3], start[4], start[5]);
            continue;
        }
        // Fly again from the step's start, exactly as far as the event.
        step[STEP_LENGTH] *= step[STEP_EVENT];
        rk4Step(world, scratch, end);
        refineEvent(world, scratch);
        step[STEP_START] += step[STEP_LENGTH];
        copy(end, start, 6);
        const t = step[STEP_START];
        if (event === OUT_OF_BOUNDS) {
            recorder?.point(t, start[0], start[1], start[2], start[3], start[4], start[5]);
            return stop(scratch, "outOfBounds", t);
        }
        if (event === DESCENDING) {
            start[2] = value;
            recorder?.point(t, start[0], start[1], start[2], start[3], start[4], start[5]);
            finishAtEvent(scratch);
            return stop(scratch, "until", t);
        }
        // end keeps the arriving velocity; start takes the bounce.
        const panel = event === GROUND ? null : world.panels[event];
        if (panel === null)
            bounceOffGround(world, scratch);
        else
            strike(panel, start);
        recorder?.point(t, start[0], start[1], start[2], end[3], end[4], end[5]);
        if (panel === null)
            recorder?.contact(t, start[0], start[1], start[2], "ground", "ground");
        else
            recorder?.contact(t, start[0], start[1], start[2], panel.id, panel.kind);
        if (panel?.absorbs)
            return stop(scratch, "absorbed", t);
        recorder?.deflect(start[3], start[4], start[5]);
        scratch.leaving = event;
        contacts += 1;
        accelerate(world, scratch, start, 3, startAcceleration, 0);
        if (panel === null) {
            if (goal === "descending")
                return stop(scratch, "until", t);
            if (!scratch.ended)
                finishAtEvent(scratch);
            if (goal === "landing")
                return stop(scratch, "until", t);
        }
        if (goal === "bounces" && contacts >= value)
            return stop(scratch, "until", t);
    }
}
function setState(state, p, v) {
    state[0] = p.x;
    state[1] = p.y;
    state[2] = p.z;
    state[3] = v.x;
    state[4] = v.y;
    state[5] = v.z;
}
/** The flight ends at the event: the ball is at scratch.start, arriving with scratch.end's velocity. */
function finishAtEvent(scratch) {
    const { result, step, start, end } = scratch;
    scratch.ended = true;
    result[END_T] = step[STEP_START];
    for (let i = 0; i < 3; i++) {
        result[END_X + i] = start[i];
        result[END_V + i] = end[3 + i];
    }
}
/** One classical RK4 step of the current step's length from scratch.start into out. */
function rk4Step(world, scratch, out) {
    const { start: s, startAcceleration, velocities: v, accelerations: k } = scratch;
    const h = scratch.step[STEP_LENGTH];
    for (let i = 0; i < 3; i++) {
        v[i] = s[3 + i];
        k[i] = startAcceleration[i];
    }
    // Stages 2-4 move half, half, then all of the step along the previous stage's acceleration.
    for (let stage = 3; stage < 12; stage += 3) {
        const reach = stage === 9 ? h : h / 2;
        for (let i = 0; i < 3; i++)
            v[stage + i] = v[i] + reach * k[stage - 3 + i];
        accelerate(world, scratch, v, stage, k, stage);
    }
    const sixth = h / 6;
    for (let i = 0; i < 3; i++) {
        out[i] = s[i] + sixth * (v[i] + 2 * v[3 + i] + 2 * v[6 + i] + v[9 + i]);
        out[3 + i] = v[i] + sixth * (k[i] + 2 * k[3 + i] + 2 * k[6 + i] + k[9 + i]);
    }
    if (scratch.rolling)
        out[5] = 0;
}
/**
 * The earliest event between scratch.start and scratch.end, located on the
 * step's cubic Hermite interpolant. Returns the event and leaves its fraction
 * of the step in scratch.step[STEP_EVENT].
 */
function findEvent(world, scratch, { goal, value, stopOutOfBounds }) {
    const { start: s, end: e } = scratch;
    const { panels, reach } = world;
    scratch.event = NO_EVENT;
    scratch.step[STEP_EVENT] = Infinity;
    if (world.ground !== null && !scratch.rolling) {
        if (s[2] > 0 && e[2] <= 0)
            consider(world, scratch, GROUND, 0, 0, 1, 0, 0);
        else if (s[2] <= 0 && e[2] < 0)
            considerHop(world, scratch);
    }
    // A sign change of the distance to a panel's plane, from a side it stops.
    // Most steps end on a wall's playing side, so the end is tested first.
    for (let i = 0; i < panels.length; i++) {
        if (i === scratch.leaving)
            continue;
        const panel = panels[i];
        const after = e[0] * panel.normalX + e[1] * panel.normalY - panel.offset;
        if (!((panel.stopsLeft && after <= 0) || (panel.stopsRight && after >= 0)))
            continue;
        const before = s[0] * panel.normalX + s[1] * panel.normalY - panel.offset;
        if ((panel.stopsLeft && before > 0 && after <= 0) || (panel.stopsRight && before < 0 && after >= 0)) {
            consider(world, scratch, i, panel.normalX, panel.normalY, 0, panel.offset, 0);
        }
    }
    scratch.leaving = NO_EVENT;
    if (stopOutOfBounds) {
        if (e[0] > reach.maxX)
            consider(world, scratch, OUT_OF_BOUNDS, 1, 0, 0, reach.maxX, 0);
        if (e[0] < reach.minX)
            consider(world, scratch, OUT_OF_BOUNDS, 1, 0, 0, reach.minX, 0);
        if (e[1] > reach.maxY)
            consider(world, scratch, OUT_OF_BOUNDS, 0, 1, 0, reach.maxY, 0);
        if (e[1] < reach.minY)
            consider(world, scratch, OUT_OF_BOUNDS, 0, 1, 0, reach.minY, 0);
    }
    if (goal === "descending" && s[2] > value && e[2] <= value)
        consider(world, scratch, DESCENDING, 0, 0, 1, value, 0);
    return scratch.event;
}
/**
 * Keeps `event` if the step reaches the plane normal . p = offset after
 * fraction `from` of the step and before the earliest event so far. The
 * normal is a unit vector; for an axis it picks out that coordinate exactly.
 */
function consider(world, scratch, event, nx, ny, nz, offset, from) {
    const { start: s, end: e, step, plane } = scratch;
    const h = step[STEP_LENGTH];
    const f = hermiteRoot(s[0] * nx + s[1] * ny + s[2] * nz, e[0] * nx + e[1] * ny + e[2] * nz, (s[3] * nx + s[4] * ny + s[5] * nz) * h, (e[3] * nx + e[4] * ny + e[5] * nz) * h, offset, from);
    if (f < step[STEP_EVENT] && admits(world, scratch, event, f)) {
        scratch.event = event;
        scratch.onPlane = true;
        step[STEP_EVENT] = f;
        plane[0] = nx;
        plane[1] = ny;
        plane[2] = nz;
        plane[PLANE_OFFSET] = offset;
    }
}
/**
 * The ball left the ground at the start of this step and is below it at the
 * end: a hop shorter than the step. It lands after the top of the hop; a hop
 * too low for the interpolant to rise above the ground lands at its top, so
 * the bounce finds the ball at rest.
 */
function considerHop(world, scratch) {
    const { start: s, end: e, startAcceleration: a0, endAcceleration: a1, step } = scratch;
    const h = step[STEP_LENGTH];
    const top = s[5] > 0 && e[5] < 0 ? hermiteRoot(s[5], e[5], a0[2] * h, a1[2] * h, 0, 0) : 0;
    if (hermite(s[2], e[2], s[5] * h, e[5] * h, top) > 0) {
        consider(world, scratch, GROUND, 0, 0, 1, 0, top);
    }
    else if (top < step[STEP_EVENT]) {
        scratch.event = GROUND;
        scratch.onPlane = false;
        step[STEP_EVENT] = top;
    }
}
/**
 * Newton's method on the length of the event substep, so the ball ends it on
 * the event's plane to rounding rather than to the interpolant's accuracy.
 */
function refineEvent(world, scratch) {
    const { step, end, plane } = scratch;
    if (!scratch.onPlane)
        return;
    for (let i = 0; i < 4; i++) {
        const miss = end[0] * plane[0] + end[1] * plane[1] + end[2] * plane[2] - plane[PLANE_OFFSET];
        const speed = end[3] * plane[0] + end[4] * plane[1] + end[5] * plane[2];
        if (Math.abs(miss) <= 1e-13 || speed === 0)
            return;
        step[STEP_LENGTH] = Math.max(0, step[STEP_LENGTH] - miss / speed);
        rk4Step(world, scratch, end);
    }
}
/**
 * Whether a plane crossing at fraction f of the step is a contact: a panel's
 * crossing must lie within its ends, at or above its bottom and below its
 * top, which rises as a parabola from the middle to the ends.
 */
function admits(world, { start: s, end: e, step }, event, f) {
    if (event < 0)
        return true;
    const panel = world.panels[event];
    const h = step[STEP_LENGTH];
    const x = hermite(s[0], e[0], s[3] * h, e[3] * h, f);
    const y = hermite(s[1], e[1], s[4] * h, e[4] * h, f);
    const along = (x - panel.midX) * panel.alongX + (y - panel.midY) * panel.alongY;
    if (!(Math.abs(along) <= panel.halfLength))
        return false;
    const z = hermite(s[2], e[2], s[5] * h, e[5] * h, f);
    const w = along / panel.halfLength;
    return z >= panel.bottom && z < panel.top + panel.rise * w * w;
}
/** Puts the ball (scratch.start, at the event) on the ground and bounces it. */
function bounceOffGround(world, scratch) {
    const s = scratch.start;
    s[2] = 0;
    s[5] = -s[5] * world.ground.restitution; // GROUND events need a ground
    if (s[5] * s[5] < 2 * world.gravity * REST_HEIGHT) {
        s[5] = 0;
        scratch.rolling = true;
    }
}
/**
 * Puts the ball on the panel's plane and, unless the panel absorbs it,
 * bounces it: the normal velocity reverses, scaled by restitution, and the
 * velocity tangent to the panel (along it and vertical) is scaled by
 * tangential. The arithmetic is exact for an axis-aligned panel.
 */
function strike(panel, s) {
    const { normalX: nx, normalY: ny } = panel;
    const miss = s[0] * nx + s[1] * ny - panel.offset;
    s[0] -= miss * nx;
    s[1] -= miss * ny;
    if (panel.absorbs)
        return;
    const { restitution, tangential } = panel;
    const normal = s[3] * nx + s[4] * ny;
    s[3] = tangential * (s[3] - normal * nx) - restitution * normal * nx;
    s[4] = tangential * (s[4] - normal * ny) - restitution * normal * ny;
    s[5] *= tangential;
}
/** The top of the flight, if the ball reaches it in this step before any event. */
function recordApex(scratch) {
    const { start: s, end: e, startAcceleration: a0, endAcceleration: a1, result, step } = scratch;
    if (!(s[5] > 0 && e[5] <= 0))
        return;
    const h = step[STEP_LENGTH];
    const f = hermiteRoot(s[5], e[5], a0[2] * h, a1[2] * h, 0, 0);
    if (f > step[STEP_EVENT])
        return;
    raiseApex(result, step[STEP_START] + f * h, hermite(s[0], e[0], s[3] * h, e[3] * h, f), hermite(s[1], e[1], s[4] * h, e[4] * h, f), hermite(s[2], e[2], s[5] * h, e[5] * h, f));
}
// ---------------------------------------------------------------------------
// Cubic Hermite interpolation on a unit interval: values p0, p1 and
// derivatives d0, d1 (already scaled by the interval's length).
function hermite(p0, p1, d0, d1, s) {
    const s2 = s * s;
    const s3 = s2 * s;
    return (2 * s3 - 3 * s2 + 1) * p0 + (s3 - 2 * s2 + s) * d0 + (3 * s2 - 2 * s3) * p1 + (s3 - s2) * d1;
}
/**
 * Where on [from, 1] the interpolant reaches `target`, given that it lies on
 * either side of it at the two ends (or on it at 1): Newton's method, falling
 * back to bisection whenever a step would leave the bracket.
 */
function hermiteRoot(p0, p1, d0, d1, target, from) {
    // The interpolant minus target as c3 s^3 + c2 s^2 + c1 s + c0, so the loop makes no calls.
    const c3 = 2 * p0 - 2 * p1 + d0 + d1;
    const c2 = 3 * p1 - 3 * p0 - 2 * d0 - d1;
    const c1 = d0;
    const c0 = p0 - target;
    const atFrom = ((c3 * from + c2) * from + c1) * from + c0;
    const rising = p1 - target > atFrom;
    let lo = from;
    let hi = 1;
    let s = from + (atFrom / (atFrom - (p1 - target))) * (1 - from);
    if (!(s > lo && s < hi))
        s = (lo + hi) / 2;
    for (let i = 0; i < 60; i++) {
        const value = ((c3 * s + c2) * s + c1) * s + c0;
        if (value === 0)
            return s;
        if ((value < 0) === rising)
            lo = s;
        else
            hi = s;
        let next = s - value / ((3 * c3 * s + 2 * c2) * s + c1);
        if (!(next > lo && next < hi))
            next = (lo + hi) / 2;
        if (Math.abs(next - s) < 1e-15)
            return next;
        s = next;
    }
    return s;
}
function createLog(withPath) {
    const contacts = [];
    let capacity = 512;
    let size = 0;
    let steps = new Float64Array(4 * capacity);
    let velocities = new Float64Array(6 * capacity); // arriving xyz, leaving xyz
    const grow = () => {
        capacity *= 2;
        const moreSteps = new Float64Array(4 * capacity);
        moreSteps.set(steps);
        steps = moreSteps;
        const moreVelocities = new Float64Array(6 * capacity);
        moreVelocities.set(velocities);
        velocities = moreVelocities;
    };
    const recorder = {
        point(t, x, y, z, vx, vy, vz) {
            if (!withPath)
                return;
            if (size === capacity)
                grow();
            writeStep(steps, 4 * size, t, x, y, z);
            writeVelocity(velocities, 6 * size, vx, vy, vz);
            writeVelocity(velocities, 6 * size + 3, vx, vy, vz);
            size += 1;
        },
        deflect(vx, vy, vz) {
            if (withPath)
                writeVelocity(velocities, 6 * (size - 1) + 3, vx, vy, vz);
        },
        contact(t, x, y, z, surface, kind) {
            contacts.push({ t, position: { x, y, z }, surface, kind });
        },
        truncate(t, x, y, z) {
            if (withPath)
                writeStep(steps, 4 * (size - 1), t, x, y, z);
        },
    };
    const path = withPath ? () => createPath(steps.slice(0, 4 * size), velocities.slice(0, 6 * size)) : null;
    return { recorder, contacts, path };
}
function writeStep(steps, at, t, x, y, z) {
    steps[at] = t;
    steps[at + 1] = x;
    steps[at + 2] = y;
    steps[at + 3] = z;
}
function writeVelocity(velocities, at, vx, vy, vz) {
    velocities[at] = vx;
    velocities[at + 1] = vy;
    velocities[at + 2] = vz;
}
/** A path through recorded points, cubic Hermite between them (exact at the points). */
function createPath(steps, velocities) {
    const count = steps.length / 4;
    const duration = steps[4 * (count - 1)];
    const write = (t, out, at) => {
        const time = Math.min(Math.max(t, 0), duration);
        let lo = 0;
        let hi = count - 1;
        while (hi - lo > 1) {
            const mid = (lo + hi) >> 1;
            if (steps[4 * mid] <= time)
                lo = mid;
            else
                hi = mid;
        }
        const t0 = steps[4 * lo];
        const span = steps[4 * hi] - t0;
        const f = span > 0 ? (time - t0) / span : 0;
        for (let axis = 0; axis < 3; axis++) {
            out[at + axis] = hermite(steps[4 * lo + 1 + axis], steps[4 * hi + 1 + axis], velocities[6 * lo + 3 + axis] * span, velocities[6 * hi + axis] * span, f);
        }
    };
    const point = new Float64Array(3);
    return {
        duration,
        steps,
        at: (t) => {
            write(t, point, 0);
            return { x: point[0], y: point[1], z: point[2] };
        },
        sample: (interval) => {
            if (!(interval > 0))
                throw new RangeError(`interval must be positive, got ${interval}`);
            const n = Math.floor(duration / interval + 1e-9) + 1;
            const out = new Float32Array(3 * n);
            for (let i = 0; i < n; i++)
                write(i * interval, out, 3 * i);
            return out;
        },
    };
}
